import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

const CHECKOUT_URL = "https://sdydqpjhqwlzaedmsncd.supabase.co/functions/v1/stripe-checkout";

export default function PricingSection() {
  const { t } = useTranslation();
  const { pricingPlans } = useLandingData();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const getPrice = (base: string) => {
    if (billing === "annual") {
      return Math.floor(parseInt(base) * 0.8).toString();
    }
    return base;
  };

  const handleCheckout = async (planId: string) => {
    setLoadingPlan(planId);
    setCheckoutError(null);
    try {
      const res = await fetch(CHECKOUT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId, billing, origin: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError(t("pricing_error"));
      }
    } catch {
      setCheckoutError(t("pricing_error_connection"));
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="precios" ref={ref} className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("pricing_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("pricing_title_1")}
            <br />
            <span className="text-amber-500">{t("pricing_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-500 text-lg max-w-xl mx-auto">
            {t("pricing_subtitle")}
          </p>

          <div className="inline-flex items-center mt-8 p-1 bg-stone-100 rounded-full">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                billing === "monthly" ? "bg-white text-stone-900" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {t("pricing_monthly")}
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                billing === "annual" ? "bg-white text-stone-900" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {t("pricing_annual")}
              <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded-full font-bold">-20%</span>
            </button>
          </div>
        </div>

        {checkoutError && (
          <div className="max-w-md mx-auto mb-8 flex items-center gap-3 bg-red-50 border border-red-100 text-red-600 px-5 py-3.5 rounded-xl text-sm">
            <i className="ri-error-warning-line text-lg flex-shrink-0" />
            <span>{checkoutError}</span>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {pricingPlans.map((plan, i) => (
            <div
              key={plan.id}
              className={`relative rounded-3xl border transition-all duration-500 ${
                plan.highlighted
                  ? "bg-stone-900 border-stone-700 lg:-mt-4 lg:pb-4"
                  : "bg-white border-stone-100"
              } ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-full whitespace-nowrap tracking-wide">
                    {t("pricing_popular_badge")}
                  </span>
                </div>
              )}

              <div className="p-8">
                <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${plan.highlighted ? "text-amber-400" : "text-stone-400"}`}>
                  {plan.name}
                </p>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className={`text-5xl font-black ${plan.highlighted ? "text-white" : "text-stone-900"}`}>
                    {getPrice(plan.price)}€
                  </span>
                  <span className={`text-sm ${plan.highlighted ? "text-stone-400" : "text-stone-400"}`}>
                    {t("pricing_per_month")}
                  </span>
                </div>
                {billing === "annual" && (
                  <p className="text-sm text-emerald-400 mb-3">
                    {t("pricing_billed_annual")} {(parseInt(getPrice(plan.price)) * 12).toLocaleString()}{t("pricing_per_year")}
                  </p>
                )}
                <p className={`text-sm leading-relaxed mb-7 ${plan.highlighted ? "text-stone-400" : "text-stone-500"}`}>
                  {plan.description}
                </p>

                <button
                  onClick={() => handleCheckout(plan.id)}
                  disabled={loadingPlan === plan.id}
                  className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed ${
                    plan.highlighted
                      ? "bg-amber-500 hover:bg-amber-400 text-white"
                      : "border border-stone-200 hover:border-stone-400 text-stone-800 hover:bg-stone-50"
                  }`}
                >
                  {loadingPlan === plan.id ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      <span>{t("pricing_redirecting")}</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-lock-2-line text-xs opacity-60" />
                      <span>{plan.cta}</span>
                    </>
                  )}
                </button>

                <p className={`text-xs text-center mt-3 ${plan.highlighted ? "text-stone-500" : "text-stone-400"}`}>
                  {t("pricing_secure")}
                </p>

                <div className={`mt-7 pt-7 border-t ${plan.highlighted ? "border-stone-700" : "border-stone-100"}`}>
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature.text} className="flex items-start gap-3">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          {feature.included ? (
                            <i className={`ri-check-line text-sm font-bold ${plan.highlighted ? "text-amber-400" : "text-emerald-500"}`} />
                          ) : (
                            <i className="ri-close-line text-sm text-stone-300" />
                          )}
                        </div>
                        <span
                          className={`text-sm leading-snug ${
                            feature.included
                              ? plan.highlighted ? "text-stone-300" : "text-stone-600"
                              : plan.highlighted ? "text-stone-600" : "text-stone-300"
                          }`}
                        >
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-stone-400 text-sm mt-8">
          {t("pricing_enterprise")}{" "}
          <a
            href="mailto:auriareceptionistai@gmail.com"
            className="text-amber-600 font-semibold hover:underline cursor-pointer"
          >
            {t("pricing_enterprise_cta")}
          </a>
        </p>
      </div>
    </section>
  );
}
